import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  Activity, 
  Clock, 
  AlertTriangle, 
  TrendingUp,
  Calendar,
  Filter,
  Download,
  Globe,
  Server
} from 'lucide-react';

// API service functions
const apiUsageAPI = {
  getSummary: async () => {
    const response = await fetch('/api/admin/usage/summary', {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch usage summary');
    return response.json();
  },
  
  getTest: async () => {
    const response = await fetch('/api/admin/usage/test', {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch test data');
    return response.json();
  },
  
  testTracking: async () => {
    const response = await fetch('/api/admin/usage/test-track', {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to test tracking');
    return response.json();
  },
  
  testSimple: async () => {
    const response = await fetch('/api/admin/usage/test-simple', {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to test simple tracking');
    return response.json();
  },
  
  clearData: async () => {
    const response = await fetch('/api/admin/usage/clear', {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to clear data');
    return response.json();
  },
  
  getSites: async () => {
    const response = await fetch('/api/sites', {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch sites');
    const data = await response.json();
    // Handle both array and object response formats
    return Array.isArray(data) ? data : data.sites || [];
  },
  
  getStats: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`/api/admin/usage/stats?${params}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch usage stats');
    return response.json();
  },
  
  getTopEndpoints: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(`/api/admin/usage/endpoints?${params}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch top endpoints');
    return response.json();
  },
  
  getHourlyUsage: async (siteId: string, startDate: string, endDate: string) => {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await fetch(`/api/admin/usage/hourly/${siteId}?${params}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch hourly usage');
    return response.json();
  }
};

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const ApiUsageReport: React.FC = () => {
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const handleTestTracking = async () => {
    try {
      await apiUsageAPI.testTracking();
      // Refresh the test data after a delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Test tracking failed:', error);
    }
  };

  const handleTestSimple = async () => {
    try {
      await apiUsageAPI.testSimple();
      // Refresh the test data after a delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Simple test failed:', error);
    }
  };

  const handleClearData = async () => {
    if (window.confirm('Are you sure you want to clear all API usage data? This action cannot be undone.')) {
      try {
        await apiUsageAPI.clearData();
        // Refresh the page after clearing
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (error) {
        console.error('Clear data failed:', error);
        alert('Failed to clear data. Please check the console for details.');
      }
    }
  };

  // Fetch usage summary
  const { data: summaryData, isLoading: summaryLoading } = useQuery(
    'apiUsageSummary',
    apiUsageAPI.getSummary,
    { 
      refetchInterval: 60000, // Refresh every 60 seconds instead of 30
      refetchOnWindowFocus: false
    }
  );

  // Fetch all sites for dropdown (independent of usage stats)
  const { data: sitesData, isLoading: sitesLoading } = useQuery(
    'sites',
    apiUsageAPI.getSites,
    { 
      refetchOnWindowFocus: false
    }
  );

  // Test query for debugging
  const { data: testData } = useQuery(
    'apiUsageTest',
    apiUsageAPI.getTest,
    { 
      enabled: true, // Enable to see what data we have
      refetchOnWindowFocus: false
    }
  );

  // Debug: log test data when available
  React.useEffect(() => {
    if (testData) {
      console.log('API Usage Test Data:', testData);
    }
  }, [testData]);

  // Fetch usage stats - get all sites initially
  const { data: statsData, isLoading: statsLoading } = useQuery(
    ['apiUsageStats', selectedSite, dateRange],
    () => apiUsageAPI.getStats({
      siteId: selectedSite || "", // Pass empty string instead of undefined
      startDate: dateRange.start,
      endDate: dateRange.end,
      limit: 20
    }),
    { enabled: !!dateRange.start && !!dateRange.end }
  );

  // Fetch top endpoints
  const { data: endpointsData, isLoading: endpointsLoading } = useQuery(
    ['apiUsageEndpoints', selectedSite],
    () => apiUsageAPI.getTopEndpoints({ 
      siteId: selectedSite || "" // Pass empty string instead of undefined
    }),
    { enabled: true }
  );

  // Fetch hourly usage for selected site
  const { data: hourlyData, isLoading: hourlyLoading } = useQuery(
    ['apiUsageHourly', selectedSite, dateRange],
    () => apiUsageAPI.getHourlyUsage(selectedSite, dateRange.start, dateRange.end),
    { enabled: !!selectedSite && !!dateRange.start && !!dateRange.end }
  );

  const summary = summaryData?.data || {};
  const stats = statsData?.data || [];
  const endpoints = endpointsData?.data || [];
  const hourly = hourlyData?.data || [];

  // Debug: log the data we're getting
  React.useEffect(() => {
    console.log('Stats data:', statsData);
    console.log('Stats array:', stats);
    console.log('Endpoints data:', endpointsData);
    console.log('Summary data:', summaryData);
    console.log('Sites data:', sitesData);
  }, [statsData, endpointsData, summaryData, sitesData]);

  // Get sites for dropdown
  const sites = Array.isArray(sitesData) ? sitesData : [];

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatResponseTime = (ms: number) => {
    return `${ms.toFixed(0)}ms`;
  };

  const formatPercentage = (num: number) => {
    return `${num.toFixed(1)}%`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Usage Report</h1>
          <p className="text-gray-600 mt-1">Monitor API usage across all sites</p>
        </div>
        <div className="flex space-x-3">
          <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Calls (24h)</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {summaryLoading ? '...' : formatNumber(summary.totalCalls24h || 0)}
              </p>
            </div>
            <div className="bg-blue-100 rounded-lg p-3">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Sites (24h)</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {summaryLoading ? '...' : formatNumber(summary.activeSites24h || 0)}
              </p>
            </div>
            <div className="bg-green-100 rounded-lg p-3">
              <Server className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Response Time</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {summaryLoading ? '...' : formatResponseTime(summary.avgResponseTime24h || 0)}
              </p>
            </div>
            <div className="bg-yellow-100 rounded-lg p-3">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Error Rate (24h)</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {summaryLoading ? '...' : formatPercentage(summary.errorRate24h || 0)}
              </p>
            </div>
            <div className="bg-red-100 rounded-lg p-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Site
            </label>
            <select
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Sites</option>
              {sites.map((site: any) => (
                <option key={site._id} value={site._id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Usage by Site Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">API Usage by Site</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={stats}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="siteName" />
            <YAxis />
            <Tooltip 
              formatter={(value: any, name: string) => [
                name === 'totalCalls' ? formatNumber(value) : 
                name === 'avgResponseTime' ? formatResponseTime(value) :
                name === 'successRate' ? formatPercentage(value) : value,
                name === 'totalCalls' ? 'Total Calls' :
                name === 'avgResponseTime' ? 'Avg Response Time' :
                name === 'successRate' ? 'Success Rate' : name
              ]}
            />
            <Bar dataKey="totalCalls" fill="#3B82F6" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top Endpoints */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Top API Endpoints</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Endpoint
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Calls
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Success Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Response Time
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {endpoints.map((endpoint: any, index: number) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {endpoint.endpoint}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      endpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
                      endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                      endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                      endpoint.method === 'DELETE' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {endpoint.method}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatNumber(endpoint.totalCalls)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatPercentage(endpoint.successRate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatResponseTime(endpoint.avgResponseTime)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hourly Usage Chart (for selected site) */}
      {selectedSite && hourly.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Hourly Usage - {stats.find((s: any) => s.siteId === selectedSite)?.siteName}
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={hourly}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="timestamp" 
                tickFormatter={(value) => new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleString()}
                formatter={(value: any) => [formatNumber(value), 'Calls']}
              />
              <Line type="monotone" dataKey="calls" stroke="#3B82F6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default ApiUsageReport;
